import { useCallback, useEffect, useState } from 'react';
import type { CalloutMode } from '@tv-casino/shared';
import {
  armGestureUnlock,
  play,
  readVolumePrefs,
  setSoundEnabled,
  soundEnabled,
  soundSupported,
  writeVolumePrefs,
  type VolumePrefs,
} from '../lib/sound.js';
import { setVoiceVolume } from '../lib/voice.js';
import { OFF_PATH, ON_PATH } from './SoundButton.js';
import styles from './DeviceAudio.module.css';

/** This phone's dealer: silent, the decisions, or every call. */
const CALLOUT_CHOICES: readonly (readonly [CalloutMode, string])[] = [
  ['off', 'Off'],
  ['essential', 'Decisions'],
  ['full', 'Every call'],
];

export interface DeviceAudioProps {
  className?: string;
  /** This device's dealer level, owned by the route so the dealer reads it live. */
  mode?: CalloutMode;
  /** Called when the player picks a level; the route remembers it. */
  onModeChange?: (mode: CalloutMode) => void;
}

/**
 * This phone's own audio, sat beside the profile disc on the seat header.
 *
 * The TV's sound is the host's to set for the room. This is not that: it is
 * the one device in this player's hand, which may have headphones in or a
 * Bluetooth speaker paired, and which nobody else at the table should be able
 * to turn up or hush. So the levels, the mute and the dealer's chattiness all
 * live in this browser's storage and go nowhere near the room state — a host
 * with the TV muted has not muted anyone's headphones.
 *
 * It starts muted, like the TV does, because a browser will not let a page
 * make noise before someone has tapped it — so the tap that unmutes is also
 * the gesture that unlocks the audio. The chip click on that first tap is the
 * proof it worked, and the proof of *where* it came out: a player who paired a
 * speaker hears which one just spoke.
 */
export function DeviceAudio({ className, mode, onModeChange }: DeviceAudioProps = {}) {
  const [open, setOpen] = useState(false);
  const [on, setOn] = useState(() => soundEnabled());
  const [volumes, setLocalVolumes] = useState<VolumePrefs>(() => readVolumePrefs());

  // A reload keeps "on" but loses the unlocked context; the next tap restores it.
  useEffect(() => armGestureUnlock(), []);

  // Whatever this device chose last time is what it plays at from the off.
  // The voice level is handed to `voice.ts`, which owns that fader.
  useEffect(() => {
    const prefs = writeVolumePrefs(readVolumePrefs());
    setVoiceVolume(prefs.voice);
  }, []);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!soundSupported()) return null;

  const setVolume = (patch: Partial<VolumePrefs>) => {
    const next = { ...volumes, ...patch };
    setLocalVolumes(next);
    const saved = writeVolumePrefs(next);
    setVoiceVolume(saved.voice);
  };

  const toggle = () => {
    const next = !on;
    setSoundEnabled(next);
    setOn(next);
    // One chip on the felt, so the player hears which speaker they are on.
    if (next) play('chip');
  };

  const label = on ? `Your sound on, ${volumes.sound}%` : 'Your sound off';

  return (
    <>
      <button
        type="button"
        className={[styles.disc, on ? styles.live : '', className].filter(Boolean).join(' ')}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`${label} — open your audio controls`}
        title="Your audio"
        onClick={() => setOpen(true)}
      >
        <svg className={styles.icon} viewBox="0 0 24 24" aria-hidden="true">
          <path d={on ? ON_PATH : OFF_PATH} />
        </svg>
      </button>

      {open && (
        <div className={styles.scrim} onClick={close}>
          <div
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label="Your audio"
            onClick={(e) => e.stopPropagation()}
          >
            <header className={styles.head}>
              <h2 className={styles.title}>Your audio</h2>
              <button type="button" className={styles.close} onClick={close} aria-label="Close">
                ×
              </button>
            </header>

            <p className={styles.hint}>
              This phone only. Headphones or a paired speaker play the table and its dealer,
              without touching the TV or anyone else's phone.
            </p>

            <button
              type="button"
              className={[styles.toggle, on ? styles.toggleOn : ''].filter(Boolean).join(' ')}
              aria-pressed={on}
              onClick={toggle}
            >
              <svg className={styles.toggleIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d={on ? ON_PATH : OFF_PATH} />
              </svg>
              {on ? 'Sound on' : 'Muted'}
            </button>

            <Slider
              label="Table"
              value={volumes.sound}
              dimmed={!on}
              onChange={(sound) => setVolume({ sound })}
            />
            <Slider
              label="Casino floor"
              value={volumes.ambience}
              dimmed={!on}
              onChange={(ambience) => setVolume({ ambience })}
            />
            <Slider
              label="Dealer"
              value={volumes.voice}
              dimmed={!on}
              onChange={(voice) => setVolume({ voice })}
            />

            {onModeChange && mode && (
              <>
                <div className={styles.chipRow} role="group" aria-label="Dealer call-outs">
                  <span className={styles.chipRowLabel}>Call-outs</span>
                  {CALLOUT_CHOICES.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={[styles.chip, mode === value ? styles.chipOn : '']
                        .filter(Boolean)
                        .join(' ')}
                      aria-pressed={mode === value}
                      onClick={() => onModeChange(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className={styles.hint}>
                  How much your dealer says — yours alone, on this phone. The TV takes the host's
                  setting.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/** A 0–100 level. Nothing leaves the device, so every drag applies as it moves. */
function Slider({
  label,
  value,
  dimmed,
  onChange,
}: {
  label: string;
  value: number;
  /** Muted: the level still moves, it just is not heard yet. */
  dimmed: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className={[styles.volume, dimmed ? styles.dimmed : ''].filter(Boolean).join(' ')}>
      <span>{label}</span>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={value}
        aria-label={label}
        aria-valuetext={`${value}%`}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className={styles.volumeValue}>{value}%</span>
    </div>
  );
}
